import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { AppConfigProvider } from './context/AppConfigContext';
import { Toaster } from 'sonner';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import AuthCallback from './pages/AuthCallback';
import PublicApproval from './pages/PublicApproval';
import PublicApprovalReview from './pages/PublicApprovalReview';
import Dashboard from './pages/Dashboard';
import StatementTracking from './pages/StatementTracking';
import BankReceipts from './pages/BankReceipts';
import Buildings from './pages/Buildings';
import BuildingDetails from './pages/BuildingDetails';
import BuildingStatement from './pages/BuildingStatement';
import Tasks from './pages/Tasks';
import Payments from './pages/Payments';
import Expenses from './pages/Expenses';
import ElevatorPage from './pages/Elevator';
import ElevatorCompanies from './pages/ElevatorCompanies';
import FireExtinguisherCompanies from './pages/FireExtinguisherCompanies';
import ElevatorCertificationCompanies from './pages/ElevatorCertificationCompanies';
import FailureReasons from './pages/FailureReasons';
import ExpenseCategories from './pages/ExpenseCategories';
import EmailTemplatesGlobal from './pages/EmailTemplatesGlobal';
import EmailCenter from './pages/EmailCenter';
import Payroll from './pages/Payroll';
import BuildingSettings from './pages/BuildingSettings';
import CleaningHistory from './pages/CleaningHistory';
import Apartments from './pages/Apartments';
import ApartmentDetails from './pages/ApartmentDetails';
import Contacts from './pages/Contacts';
import Statements from './pages/Statements';
import AnnualStatement from './pages/AnnualStatement';
import ReserveFundStatement from './pages/ReserveFundStatement';
import ReserveSplitFix from './pages/ReserveSplitFix';
import ApartmentStatement from './pages/ApartmentStatement';
import InsuranceCompanies from './pages/InsuranceCompanies';
import Vendors from './pages/Vendors';
import VendorDetails from './pages/VendorDetails';
import BillVendors from './pages/BillVendors';
import BillVendorDetails from './pages/BillVendorDetails';
import CompanySettings from './pages/CompanySettings';
import Settings from './pages/Settings';
import BackupsPage from './pages/BackupsPage';
import Notifications from './pages/Notifications';
import Users from './pages/Users';
import AgendaTemplates from './pages/AgendaTemplates';
import CleaningManagement from './pages/CleaningManagement';
import Finance from './pages/Finance';
import SyncAudit from './pages/SyncAudit';
import Sales from './pages/Sales';
import Purchases from './pages/Purchases';
import FinanceDetail from './pages/FinanceDetail';
// Phase 1016 — BillsV2 import removed. Route now renders BillsRebuildingPlaceholder.
// Phase 1020 — Password management + admin users pages.
import ChangePassword from './pages/ChangePassword';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AdminUsers from './pages/AdminUsers';
import AdminDataSync from './pages/AdminDataSync';
import OwnerFeedbackOverview from './pages/OwnerFeedbackOverview';
import PublicOwnerFeedback from './pages/PublicOwnerFeedback';
import { PreviewBanner } from './components/PreviewBanner';
import ClerkPage from './pages/ClerkPage';
import AdvancesPage from './pages/AdvancesPage';
import CalendarPage from './pages/CalendarPage';
import EnhancedCalendarTasksPage from './pages/EnhancedCalendarTasksPage';
import TasksListPage from './pages/TasksListPage';
import Privacy from './pages/Privacy';
import PdfViewerHost from './components/PdfViewerHost';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // If user data passed from location state (from AuthCallback), use it
  if (location.state?.user) {
    // Clerks are only ever allowed on /clerk (and auth/account routes).
    const u = location.state.user;
    if (u?.role === 'building_payments_clerk' && !_isClerkPath(location.pathname)) {
      return <Navigate to="/clerk" replace />;
    }
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-zinc-500">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Phase 1024 — The clerk role has a dedicated minimal shell at /clerk.
  // Any attempt to open an admin/staff route is redirected to the clerk
  // shell. The real security gate is server-side (middleware) — this is
  // only a UX nicety so the clerk doesn't see a wall of 403s.
  if (user.role === 'building_payments_clerk' && !_isClerkPath(location.pathname)) {
    return <Navigate to="/clerk" replace />;
  }

  return children;
};

function _isClerkPath(path) {
  return (
    path === '/clerk' ||
    path.startsWith('/clerk/') ||
    path.startsWith('/account/') ||
    path === '/login'
  );
}

// Phase 1016 — Bills module UI removed per user request. Route intentionally
// renders this placeholder so bookmarked /bills links show a clear message
// instead of the old UI. Backend endpoints and DB data are untouched.
function BillsRebuildingPlaceholder() {
  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-6" data-testid="bills-rebuilding-placeholder">
      <div className="max-w-md w-full bg-white border border-zinc-200 p-10 text-center">
        <div className="text-4xl mb-4">🧾</div>
        <h1 className="text-xl font-semibold text-zinc-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Bills is being rebuilt</h1>
        <p className="text-sm text-zinc-500 leading-relaxed">
          The Bills module has been removed from the interface and is being
          redesigned from scratch. Your bills, payments, vendors and history
          are safely preserved in the database and will reappear in the new
          module.
        </p>
        <a href="/" className="inline-block mt-6 px-4 h-9 leading-9 text-xs font-semibold uppercase tracking-wider bg-zinc-900 text-white hover:bg-zinc-700" data-testid="bills-rebuilding-back">Back to Dashboard</a>
      </div>
    </div>
  );
}

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  // This must be synchronous - detect during render, NOT in useEffect
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
    <PreviewBanner />
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* Phase 1019 — Public /register route REMOVED. Self-registration
          is disabled; only admins can create new users. Any attempt to
          reach /register is redirected to /login. */}
      <Route path="/register" element={<Navigate to="/login" replace />} />
      {/* Phase 1020 — Forgot / Reset password (public — token-gated) */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Phase 1020 — Change password (authenticated) */}
      <Route path="/account/change-password"
             element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
      {/* Phase 1020 — Admin users management (admin-only enforced on backend) */}
      <Route path="/admin/users"
             element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/data-sync"
             element={<ProtectedRoute><AdminDataSync /></ProtectedRoute>} />
      {/* Phase 1024 — Dedicated BUILDING_PAYMENTS_CLERK shell. Server-side
          middleware enforces the strict role scope; this is the UX surface. */}
      <Route path="/clerk"
             element={<ProtectedRoute><ClerkPage /></ProtectedRoute>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/public/approval/:token" element={<PublicApproval />} />
      <Route path="/feedback/:token" element={<PublicOwnerFeedback />} />
      <Route path="/owner-feedback" element={<ProtectedRoute><OwnerFeedbackOverview /></ProtectedRoute>} />
      <Route path="/review/:token" element={<PublicApprovalReview />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/statement-tracking" element={<ProtectedRoute><StatementTracking /></ProtectedRoute>} />
      <Route path="/bank-receipts" element={<ProtectedRoute><BankReceipts /></ProtectedRoute>} />
      <Route
        path="/buildings"
        element={
          <ProtectedRoute>
            <Buildings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts"
        element={
          <ProtectedRoute>
            <Contacts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/buildings/:buildingId"
        element={
          <ProtectedRoute>
            <BuildingDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/buildings/:buildingId/statement"
        element={
          <ProtectedRoute>
            <BuildingStatement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/buildings/:buildingId/apartments"
        element={
          <ProtectedRoute>
            <Apartments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/apartments/:apartmentId"
        element={
          <ProtectedRoute>
            <ApartmentDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <Tasks />
          </ProtectedRoute>
        }
      />
      <Route
        path="/insurance-companies"
        element={
          <ProtectedRoute>
            <InsuranceCompanies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payments"
        element={
          <ProtectedRoute>
            <Payments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expenses"
        element={
          <ProtectedRoute>
            <Expenses />
          </ProtectedRoute>
        }
      />
      <Route
        path="/elevator"
        element={
          <ProtectedRoute>
            <ElevatorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/elevator-companies"
        element={
          <ProtectedRoute>
            <ElevatorCompanies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fire-extinguisher-companies"
        element={
          <ProtectedRoute>
            <FireExtinguisherCompanies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/elevator-certification-companies"
        element={
          <ProtectedRoute>
            <ElevatorCertificationCompanies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/failure-reasons"
        element={
          <ProtectedRoute>
            <FailureReasons />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expense-categories"
        element={
          <ProtectedRoute>
            <ExpenseCategories />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-templates"
        element={
          <ProtectedRoute>
            <EmailTemplatesGlobal />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-center"
        element={
          <ProtectedRoute>
            <EmailCenter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payroll"
        element={
          <ProtectedRoute>
            <Payroll />
          </ProtectedRoute>
        }
      />
      <Route
        path="/building-settings"
        element={
          <ProtectedRoute>
            <BuildingSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cleaning-history"
        element={<Navigate to="/cleaning-management?tab=cleaningHistory" replace />}
      />
      <Route
        path="/cleaning-management"
        element={
          <ProtectedRoute>
            <CleaningManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales"
        element={
          <ProtectedRoute>
            <Sales />
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchases"
        element={
          <ProtectedRoute>
            <Purchases />
          </ProtectedRoute>
        }
      />
      <Route
        path="/buildings/:buildingId/annual-statement"
        element={
          <ProtectedRoute>
            <AnnualStatement />
          </ProtectedRoute>
        }
      />
      <Route path="/reserve-split-fix" element={<ProtectedRoute><ReserveSplitFix /></ProtectedRoute>} />
      <Route
        path="/buildings/:buildingId/reserve-fund-statement"
        element={
          <ProtectedRoute>
            <ReserveFundStatement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/statements"
        element={
          <ProtectedRoute>
            <Statements />
          </ProtectedRoute>
        }
      />
      <Route
        path="/apartment-statement"
        element={
          <ProtectedRoute>
            <ApartmentStatement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendors"
        element={
          <ProtectedRoute>
            <Vendors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendors/:vendorId"
        element={
          <ProtectedRoute>
            <VendorDetails />
          </ProtectedRoute>
        }
      />
      {/* Phase 215 — Bill Vendors are SEPARATE from Building-Management
          Vendors (utility/service providers vs elevator/insurance/maintenance). */}
      <Route
        path="/bill-vendors"
        element={
          <ProtectedRoute>
            <BillVendors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bill-vendors/:vendorId"
        element={
          <ProtectedRoute>
            <BillVendorDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <Users />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agenda-templates"
        element={
          <ProtectedRoute>
            <AgendaTemplates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/company-settings"
        element={
          <ProtectedRoute>
            <CompanySettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/backups"
        element={
          <ProtectedRoute>
            <BackupsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance"
        element={
          <ProtectedRoute>
            <Finance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sync-audit"
        element={
          <ProtectedRoute>
            <SyncAudit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance/allocations/:type"
        element={
          <ProtectedRoute>
            <FinanceDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance/:type"
        element={
          <ProtectedRoute>
            <FinanceDetail />
          </ProtectedRoute>
        }
      />
      {/* Phase 1016 — Bills module UI removed per user request. The
          module is being rebuilt from scratch. Backend endpoints
          (/bills2/*, /bills/*, /recurring-bills/*) and DB collections
          (bills, bill_payments, bill_assets, bill_vendors, expenses,
          etc.) are intact — no data was deleted. This route now
          renders a minimal placeholder so any bookmarked /bills link
          shows a clear "rebuilding" message instead of the old UI. */}
      <Route
        path="/bills"
        element={
          <ProtectedRoute>
            <BillsRebuildingPlaceholder />
          </ProtectedRoute>
        }
      />
      {/* Phase 426 — Legacy /bills/legacy, /bills/category and /bills/:billId routes removed.
          All bills now live in the asset-centric Bills v2 module at /bills. */}
      <Route
        path="/advances"
        element={
          <ProtectedRoute>
            <AdvancesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar/tasks"
        element={
          <ProtectedRoute>
            <EnhancedCalendarTasksPage />
          </ProtectedRoute>
        }
      />
      {/* Phase 996 — Dedicated Tasks Management (flat list, filters, edit/delete). */}
      <Route
        path="/calendar/tasks-list"
        element={
          <ProtectedRoute>
            <TasksListPage />
          </ProtectedRoute>
        }
      />
      {/* Phase 1019 — Root URL is auth-gated. Unauthenticated users are
          bounced straight to /login (1-hop) so no protected content is
          ever attempted. The catch-all also wraps in ProtectedRoute so
          typos/unknown paths never expose the app shell. */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Navigate to="/dashboard" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <Navigate to="/dashboard" replace />
          </ProtectedRoute>
        }
      />
    </Routes>
    </>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <AppConfigProvider>
          <BrowserRouter>
            <AppRouter />
            {/* Phase 735.30 — Global in-app PDF viewer. Listens for
                `emergent:open-pdf` events from `pdfUtils.openPdfInBrowser`
                and renders the PDF in an iframe on the current page (no
                new tab → no popup-blocker interaction → no adblocker URL
                pattern matching against navigations). */}
            <PdfViewerHost />
            <Toaster 
              position="top-right" 
              richColors 
              toastOptions={{
                style: {
                  borderRadius: 0,
                  fontFamily: 'IBM Plex Sans, sans-serif'
                }
              }}
            />
          </BrowserRouter>
        </AppConfigProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
